import type { CompanyFounder, CompanyProfile } from "@scoach/types";
import { useState } from "react";

import { safeHref } from "../../lib/safeHref.ts";
import { companiesApi } from "./api.ts";

/**
 * Renders a researched {@link CompanyProfile} for review, reusing the summary
 * card styling (`.sum-card` / `.sum-h3` / `.sum-list`). Shown on the meeting
 * setup page, the Quick-live modal, the live screen dossier, and the Clients
 * screen. Structured sections (not raw markdown) so it matches the card system;
 * `profile.markdown` remains available on the object for copy/export.
 */
export interface CompanyProfileCardProps {
  profile: CompanyProfile;
  /** Re-research the company (force refresh). */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** When provided, the card is editable — called with the corrected profile after save. */
  onChange?: (p: CompanyProfile) => void;
}

const EDIT_INPUT: React.CSSProperties = {
  width: "100%",
  padding: "7px 10px",
  borderRadius: 8,
  border: "1px solid var(--border)",
  fontSize: 13,
  color: "var(--text-1)",
  background: "var(--surface)",
};
const EDIT_LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--text-3)",
  display: "block",
  marginBottom: 3,
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  color: "var(--text-3)",
  padding: "8px 16px 4px",
};

function Chips({ items }: { items: string[] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 16px 4px" }}>
      {items.map((x, i) => (
        <span
          key={i}
          dir="auto"
          style={{
            fontSize: 12,
            padding: "2px 8px",
            borderRadius: 12,
            background: "var(--bg-2)",
            color: "var(--text-2)",
          }}
        >
          {x}
        </span>
      ))}
    </div>
  );
}

export function CompanyProfileCard({
  profile,
  onRefresh,
  refreshing,
  onChange,
}: CompanyProfileCardProps) {
  const [editing, setEditing] = useState(false);

  if (editing && onChange) {
    return (
      <ProfileEditForm
        profile={profile}
        onCancel={() => setEditing(false)}
        onSaved={(p) => {
          onChange(p);
          setEditing(false);
        }}
      />
    );
  }

  const { funding } = profile;
  const metaBits = [
    profile.industry,
    profile.hq,
    profile.yearEstablished,
    profile.employees && `${profile.employees} employees`,
    profile.annualRevenue && `${profile.annualRevenue} revenue`,
  ].filter(Boolean);

  return (
    <section className="sum-card">
      <div className="sum-card-head">
        <h3 className="sum-h3">
          <span className="dot" style={{ background: "var(--gc-blue)" }} /> {profile.name}
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {profile.website && (
            <a
              href={safeHref(profile.website)}
              target="_blank"
              rel="noopener noreferrer"
              className="sum-meta"
              style={{ color: "var(--gc-blue)" }}
            >
              {profile.website.replace(/^https?:\/\/(www\.)?/, "")}
            </a>
          )}
          {profile.linkedinUrl && (
            <a
              href={safeHref(profile.linkedinUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="sum-meta"
              style={{ color: "var(--gc-blue)" }}
            >
              LinkedIn
            </a>
          )}
          {onChange && (
            <button type="button" className="ghost-btn xs" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
          {onRefresh && (
            <button
              type="button"
              className="ghost-btn xs"
              onClick={onRefresh}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          )}
        </div>
      </div>

      {(onChange || onRefresh) && (
        <div style={{ padding: "0 16px 8px", fontSize: 11.5, color: "var(--text-3)" }}>
          Not correct?{" "}
          {onChange && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--gc-blue)",
                font: "inherit",
                textDecoration: "underline",
              }}
            >
              Edit the details
            </button>
          )}
          {onChange && onRefresh ? " or " : ""}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--gc-blue)",
                font: "inherit",
                textDecoration: "underline",
              }}
            >
              re-research from Apollo + web
            </button>
          )}
          .
        </div>
      )}

      {metaBits.length > 0 && (
        <div style={{ padding: "0 16px 8px", fontSize: 12, color: "var(--text-3)" }} dir="auto">
          {metaBits.join(" · ")}
        </div>
      )}

      {profile.openPositions ? (
        <div style={{ padding: "0 16px 10px" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 700,
              color: "var(--gc-green, #137333)",
              background: "var(--gc-green-50, #e6f4ea)",
              border: "1px solid var(--gc-green, #137333)",
              borderRadius: 999,
              padding: "3px 12px",
            }}
          >
            📣 {profile.openPositions} open positions
            {profile.openPositionsUrl && (
              <a
                href={safeHref(profile.openPositionsUrl)}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "var(--gc-blue, #1a73e8)",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                · careers ↗
              </a>
            )}
          </span>
        </div>
      ) : null}

      {profile.overview && (
        <div
          dir="auto"
          style={{ padding: "0 16px 12px", fontSize: 14, color: "var(--text-1)", lineHeight: 1.5 }}
        >
          {profile.overview}
        </div>
      )}

      {profile.founders.length > 0 && (
        <>
          <div style={LABEL}>FOUNDERS</div>
          <ul className="sum-list">
            {profile.founders.map((f, i) => (
              <li key={i} dir="auto">
                <strong>{f.name}</strong>
                {f.role ? ` — ${f.role}` : ""}
                {f.background ? `. ${f.background}` : ""}
                {f.linkedin && (
                  <>
                    {" "}
                    <a
                      href={safeHref(f.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--gc-blue)" }}
                    >
                      LinkedIn
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {funding &&
        (funding.totalRaised || funding.rounds.length > 0 || funding.investors.length > 0) && (
          <div
            style={{
              margin: "12px 16px 4px",
              padding: "10px 12px",
              borderRadius: 8,
              background: "var(--bg-2)",
              border: "1px solid var(--border, rgba(0,0,0,0.08))",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                color: "var(--gc-blue)",
                marginBottom: 4,
              }}
            >
              FUNDING
            </div>
            {funding.totalRaised && (
              <div style={{ fontSize: 13, color: "var(--text-1)" }}>
                <span style={{ fontWeight: 600 }}>Total raised: </span>
                {funding.totalRaised}
              </div>
            )}
            {funding.rounds.length > 0 && (
              <ul className="sum-list">
                {funding.rounds.map((r, i) => (
                  <li key={i} dir="auto">
                    {[r.round, r.amount, r.date].filter(Boolean).join(" · ")}
                    {r.leadInvestors && r.leadInvestors.length > 0
                      ? ` — ${r.leadInvestors.join(", ")}`
                      : ""}
                  </li>
                ))}
              </ul>
            )}
            {funding.investors.length > 0 && (
              <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 6 }} dir="auto">
                <span style={{ fontWeight: 600 }}>Investors: </span>
                {funding.investors.join(", ")}
              </div>
            )}
          </div>
        )}

      {profile.techStack.length > 0 && (
        <>
          <div style={LABEL}>TECHNOLOGY STACK</div>
          <Chips items={profile.techStack} />
        </>
      )}

      {profile.competitors.length > 0 && (
        <>
          <div style={LABEL}>COMPETITORS</div>
          <Chips items={profile.competitors} />
        </>
      )}

      {profile.cloudChallenges.length > 0 && (
        <>
          <div style={LABEL}>POTENTIAL CLOUD CHALLENGES</div>
          <ul className="sum-list">
            {profile.cloudChallenges.map((x, i) => (
              <li key={i} dir="auto">
                {x}
              </li>
            ))}
          </ul>
        </>
      )}

      {profile.engagementContext && (
        <>
          <div style={LABEL}>ENGAGEMENT CONTEXT</div>
          <div
            dir="auto"
            style={{
              padding: "0 16px 12px",
              fontSize: 13,
              color: "var(--text-2)",
              lineHeight: 1.5,
            }}
          >
            {profile.engagementContext}
          </div>
        </>
      )}

      {profile.sources.length > 0 && (
        <div
          style={{
            padding: "0 16px 14px",
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            fontSize: 11,
          }}
        >
          {profile.sources.map((src, i) => (
            <a
              key={i}
              href={safeHref(src.href)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--gc-blue)" }}
              title={src.title}
            >
              {src.source || src.title}
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

/** Inline editor for the key profile fields (name, description, founders, meta).
 * Persists via PATCH /companies/:slug and returns the corrected profile. */
function ProfileEditForm({
  profile,
  onCancel,
  onSaved,
}: {
  profile: CompanyProfile;
  onCancel: () => void;
  onSaved: (p: CompanyProfile) => void;
}) {
  const [name, setName] = useState(profile.name ?? "");
  const [website, setWebsite] = useState(profile.website ?? "");
  const [linkedinUrl, setLinkedinUrl] = useState(profile.linkedinUrl ?? "");
  const [industry, setIndustry] = useState(profile.industry ?? "");
  const [hq, setHq] = useState(profile.hq ?? "");
  const [year, setYear] = useState(profile.yearEstablished ?? "");
  const [employees, setEmployees] = useState(profile.employees ?? "");
  const [revenue, setRevenue] = useState(profile.annualRevenue ?? "");
  const [overview, setOverview] = useState(profile.overview ?? "");
  const [founders, setFounders] = useState<CompanyFounder[]>(
    profile.founders?.length ? profile.founders.map((f) => ({ ...f })) : [],
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  function setFounder(i: number, patch: Partial<CompanyFounder>) {
    setFounders((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  }

  async function save() {
    setSaving(true);
    setErr("");
    try {
      const updated = await companiesApi.update(profile.slug, {
        name,
        website,
        linkedinUrl,
        industry,
        hq,
        yearEstablished: year,
        employees,
        annualRevenue: revenue,
        overview,
        founders,
      });
      onSaved(updated);
    } catch (e) {
      setErr((e as Error).message || "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  }

  const field = (label: string, value: string, set: (v: string) => void) => (
    <label>
      <span style={EDIT_LABEL}>{label}</span>
      <input style={EDIT_INPUT} value={value} dir="auto" onChange={(e) => set(e.target.value)} />
    </label>
  );

  return (
    <section className="sum-card">
      <div className="sum-card-head">
        <h3 className="sum-h3">
          <span className="dot" style={{ background: "var(--gc-blue)" }} /> Edit company details
        </h3>
      </div>

      <div style={{ padding: "0 16px 12px", display: "grid", gap: 10 }}>
        {field("Company name", name, setName)}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {field("Website", website, setWebsite)}
          {field("LinkedIn URL", linkedinUrl, setLinkedinUrl)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {field("Industry", industry, setIndustry)}
          {field("HQ / location", hq, setHq)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          {field("Founded", year, setYear)}
          {field("Employees", employees, setEmployees)}
          {field("Revenue", revenue, setRevenue)}
        </div>

        <label>
          <span style={EDIT_LABEL}>Description</span>
          <textarea
            style={{ ...EDIT_INPUT, lineHeight: 1.5, resize: "vertical" }}
            rows={4}
            dir="auto"
            value={overview}
            onChange={(e) => setOverview(e.target.value)}
          />
        </label>

        <div>
          <span style={EDIT_LABEL}>Founders</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {founders.map((f, i) => (
              <div
                key={i}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: 8,
                  display: "grid",
                  gap: 6,
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 6 }}>
                  <input
                    style={EDIT_INPUT}
                    placeholder="Name"
                    value={f.name}
                    dir="auto"
                    onChange={(e) => setFounder(i, { name: e.target.value })}
                  />
                  <input
                    style={EDIT_INPUT}
                    placeholder="Role"
                    value={f.role ?? ""}
                    dir="auto"
                    onChange={(e) => setFounder(i, { role: e.target.value })}
                  />
                  <button
                    type="button"
                    className="ghost-btn xs danger"
                    onClick={() => setFounders((fs) => fs.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </div>
                <input
                  style={EDIT_INPUT}
                  placeholder="Background"
                  value={f.background ?? ""}
                  dir="auto"
                  onChange={(e) => setFounder(i, { background: e.target.value })}
                />
                <input
                  style={EDIT_INPUT}
                  placeholder="LinkedIn URL"
                  value={f.linkedin ?? ""}
                  onChange={(e) => setFounder(i, { linkedin: e.target.value })}
                />
              </div>
            ))}
            <button
              type="button"
              className="ghost-btn xs"
              onClick={() => setFounders((fs) => [...fs, { name: "" }])}
              style={{ alignSelf: "flex-start" }}
            >
              + Add founder
            </button>
          </div>
        </div>

        {err && (
          <div style={{ fontSize: 12, color: "var(--gc-red, #d93025)" }} role="alert">
            {err}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="ghost-btn" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="pill-btn primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save corrections"}
          </button>
        </div>
      </div>
    </section>
  );
}
